import React, { useEffect } from 'react';
import { useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

const AddUpdateFuelTypeModal = ({ onClose, refetch, fuelTypeId = null, initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  // Form state
  const [formData, setFormData] = useState({
    fuelName: initialData?.fuel_name || '',
    fuelForm: initialData?.fuel_form || '',
    fuelFormula: initialData?.formula || '',
  });

  const validate = (values) => {
    const newErrors = {};

    // Fuel Name validation
    if (!values.fuelName.trim()) {
      newErrors.fuelName = 'Fuel name is required';
    } else if (values.fuelName.length < 2) {
      newErrors.fuelName = 'Fuel name must be at least 2 characters';
    }

    // Fuel Form validation
    if (!values.fuelForm.trim()) {
      newErrors.fuelForm = 'Fuel form is required';
    }

    // Fuel Formula validation
    if (!values.fuelFormula.trim()) {
      newErrors.fuelFormula = 'Fuel formula is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        fuelTypeId: fuelTypeId || 0,
        ...formData
      };
      const response = await api.post('/admin/CreateUpdateFuelType', payload);
      if (response.data.status == '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch?.();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }

      onClose();
      refetch?.();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="fuelName" className="text-sm font-medium">
              Fuel Name
            </label>
            <TextField
              size="small"
              id="fuelName"
              name="fuelName"
              value={formData.fuelName}
              onChange={handleInputChange}
              placeholder="Enter fuel name"
              disabled={isSubmitting}
              error={Boolean(errors.fuelName)}
              className={'w-full'}
            />
            {errors.fuelName && (
              <p className="text-sm text-red-500 mt-1">{errors.fuelName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="fuelForm" className="text-sm font-medium block">Fuel Form</label>
            <FormControl fullWidth size="small">
              <InputLabel id="fuelForm-label">Fuel Form</InputLabel>
              <Select
                labelId="fuelForm-label"
                id="fuelForm"
                name="fuelForm"
                value={formData.fuelForm}
                onChange={handleInputChange}
                disabled={isSubmitting}
                error={Boolean(errors.fuelForm)}
                className={'w-full'}
              >
                <MenuItem value="LIQUID">LIQUID</MenuItem>
                <MenuItem value="MASS">MASS</MenuItem>
                <MenuItem value="GAS">GAS</MenuItem>
              </Select>
            </FormControl>
            {errors.fuelForm && (
              <p className="text-sm text-red-500 mt-1">{errors.fuelForm}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="fuelFormula" className="text-sm font-medium block">Fuel Formula</label>
            <TextField
              size='small'
              id="fuelFormula"
              name="fuelFormula"
              value={formData.fuelFormula}
              onChange={handleInputChange}
              placeholder="Enter fuel formula"
              disabled={isSubmitting}
              error={Boolean(errors.fuelFormula)}
              className={'w-full'}
            />
            {errors.fuelFormula && (
              <p className="text-sm text-red-500 mt-1">{errors.fuelFormula}</p>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={fuelTypeId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateFuelTypeModal;